import { useEffect, useState } from "react";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../api/support.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Notification {
  id: string;
  title: string;
  body: string;
  status: string;
  notificationType: string;
  actionUrl: string | null;
  createdAt: string;
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: "text-yellow-400",
  DELIVERED: "text-blue-400",
  READ: "text-gray-500",
  FAILED: "text-red-400",
};

export function SupportNotificationsPage() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const reload = () => {
    void listNotifications(token, { limit: 100 }).then((d) => {
      setNotifications((d as { items: Notification[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const markRead = async (id: string) => {
    await markNotificationRead(token, id);
    reload();
  };

  const markAll = async () => {
    setMarking(true);
    await markAllNotificationsRead(token);
    setMarking(false);
    reload();
  };

  const unread = notifications.filter((n) => n.status !== "READ").length;

  return (
    <Layout>
      <div className="mx-auto max-w-4xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Notifications</h1>
            {unread > 0 && <p className="mt-0.5 text-sm text-indigo-400">{unread} unread</p>}
          </div>
          {unread > 0 && (
            <button
              onClick={() => void markAll()}
              disabled={marking}
              className="rounded bg-gray-800 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 disabled:opacity-50"
            >
              {marking ? "Marking..." : "Mark all read"}
            </button>
          )}
        </div>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-2">
            {notifications.length === 0 && <p className="text-gray-400">No notifications.</p>}
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`rounded-lg border bg-gray-900 p-4 ${n.status === "READ" ? "border-gray-800 opacity-60" : "border-gray-700"}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400">
                        {n.notificationType}
                      </span>
                      <span className={`text-xs ${STATUS_COLOR[n.status] ?? "text-gray-400"}`}>
                        {n.status}
                      </span>
                    </div>
                    <p className="mt-1 font-medium text-white">{n.title}</p>
                    <p className="mt-0.5 text-sm text-gray-400">{n.body}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <span className="text-xs text-gray-600">
                      {new Date(n.createdAt).toLocaleDateString()}
                    </span>
                    {n.status !== "READ" && (
                      <button
                        onClick={() => void markRead(n.id)}
                        className="rounded bg-indigo-900/50 px-2 py-0.5 text-xs text-indigo-300 hover:bg-indigo-900"
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
